import * as Cookies from 'js-cookie';
import { ConsentReceiver } from '../components/consentReceiver';
import { ConsentState } from './consentState';

type KnownCookies = {
  [consentState in ConsentState]: string[]
};

export class ConsentService {
  private static consentServiceInstance: ConsentService;
  private static readonly cookieName: string = 'BAB_CONSENT_STATES';
  private static readonly cookieOptions: Cookies.CookieAttributes = { path: '/', sameSite: 'strict', secure: true };
  private static readonly cookieLifetime: number = 365; // number of days in a year
  private static readonly knownCookies: KnownCookies = {
    [ConsentState.ESSENTIAL]: ['ps_widget_token'],
    [ConsentState.FUNCTIONAL]: [],
    [ConsentState.ANALYTICS]: ['_ga', '_gid', '_gat', 'IDE'],
    [ConsentState.ADVERTISING]: [],
  };

  private readonly consentReceivers: ConsentReceiver[] = [];

  public static get instance(): ConsentService {
    if (!ConsentService.consentServiceInstance) {
      ConsentService.consentServiceInstance = new ConsentService();
    }

    return ConsentService.consentServiceInstance;
  }

  public get consentStates(): ConsentState[] {
    const { cookieName } = ConsentService;

    return Cookies.getJSON(cookieName) as ConsentState[];
  }

  public set consentStates(consentStates: ConsentState[]) {
    if (this.consentIsSet && this.checkConsents(consentStates)) {
      return;
    }

    const { cookieName, cookieLifetime: expires } = ConsentService;
    const cookieOptions = { ...ConsentService.cookieOptions, expires };

    Cookies.set(cookieName, consentStates, cookieOptions);
    this.callConsentHandlers();
    this.removeNonConsentedCookies(consentStates);
  }

  public set consentsForSession(consentStates: ConsentState[]) {
    if (this.consentIsSet && this.checkConsents(consentStates)) {
      return;
    }

    const { cookieName, cookieOptions } = ConsentService;

    Cookies.set(cookieName, consentStates, cookieOptions);
    this.callConsentHandlers();
    this.removeNonConsentedCookies(consentStates);
  }

  public get consentIsSet(): boolean {
    const { cookieName } = ConsentService;

    return !!Cookies.get(cookieName);
  }

  public checkConsents(consentStatesB: ConsentState[]): boolean {
    const { consentStates: consentStatesA } = this;
    const a = JSON.stringify([...consentStatesA].sort());
    const b = JSON.stringify([...consentStatesB].sort());

    return a === b;
  }

  public checkConsent(consentState: ConsentState): boolean {
    const { consentStates } = this;

    return consentStates.includes(consentState);
  }

  public removeNonConsentedCookies(consentStates: ConsentState[]): void {
    const { knownCookies } = ConsentService;

    Object.keys(knownCookies).forEach((consentState: ConsentState) => {
      if (!consentStates.includes(consentState)) {
        knownCookies[consentState].forEach((cookieName) => {
          Cookies.remove(cookieName, { path: '/' });
        });
      }
    });
  }

  public registerReceiver(receiver: ConsentReceiver): void {
    this.consentReceivers.push(receiver);
  }

  private callConsentHandlers() {
    this.consentReceivers.forEach((receiver) => {
      receiver.handleConsentChange();
    });
  }
}
